
import { useProducts } from '../../../hooks/useProducts';
import type { CartItem } from '../../../utils/cart';

interface CartItemRowProps {
  item: CartItem;
  onQuantityChange: (id: number, quantity: number) => void;
  onRemove: (id: number) => void;
}

export default function CartItemRow({ item, onQuantityChange, onRemove }: CartItemRowProps) {
  const { products } = useProducts();
  const product = products.find((p) => parseInt(p.id) === item.id);
  const maxStock = product?.stock_quantity || 0;
  const isOverStock = item.quantity > maxStock;
  const lineTotal = item.price * item.quantity;

  return (
    <div className="group flex gap-4 sm:gap-5 p-4 sm:p-5 rounded-xl hover:bg-[#F9F6F2]/60 transition-all duration-300">
      {/* Image */}
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[#F5F0EB]">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                {item.name}
              </h3>
              {product?.category && (
                <p className="text-[11px] sm:text-xs text-[#D4A574] font-medium uppercase tracking-wider mt-0.5">
                  {product.category}
                </p>
              )}
            </div>
            <button
              onClick={() => onRemove(item.id)}
              className="w-7 h-7 flex items-center justify-center rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all duration-200 cursor-pointer flex-shrink-0 opacity-0 group-hover:opacity-100 sm:opacity-100"
              title="Supprimer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {isOverStock && (
            <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
              <i className="ri-error-warning-line text-xs w-3 h-3 flex items-center justify-center"></i>
              Stock limité : {maxStock} disponible{maxStock > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Bottom row: price + quantity + total */}
        <div className="flex items-center justify-between mt-3 gap-3">
          <span className="text-xs sm:text-sm text-gray-500">
            ${item.price.toFixed(2)}
          </span>

          {/* Quantity */}
          <div className="flex items-center bg-[#F5F0EB] rounded-full overflow-hidden">
            <button
              onClick={() => onQuantityChange(item.id, item.quantity - 1)}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-[#143D28] hover:bg-[#E8E0D6] transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line text-sm"></i>
            </button>
            <span className="w-8 text-center text-sm font-semibold text-gray-900">
              {item.quantity}
            </span>
            <button
              onClick={() => onQuantityChange(item.id, item.quantity + 1)}
              disabled={item.quantity >= maxStock}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-[#143D28] hover:bg-[#E8E0D6] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line text-sm"></i>
            </button>
          </div>

          <span className="text-sm sm:text-base font-bold text-gray-900 min-w-[60px] text-right">
            ${lineTotal.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}
